import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'

interface Project {
  project_id: string
  name: string
  industry: string
  status: string
  created_at: number
  first_upload_at?: number
}

const INDUSTRIES = [
  'Insurance', 'Financial Services', 'Healthcare',
  'Consumer Brands', 'B2B / SaaS', 'Government / Public', 'Other',
]

export function ProjectsListPage() {
  const navigate = useNavigate()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [newName, setNewName] = useState('')
  const [newIndustry, setNewIndustry] = useState('')

  const load = () => {
    const token = localStorage.getItem('echo_session')
    if (!token) { setLoading(false); return }
    fetch('/api/projects', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() as Promise<Project[]> : [])
      .then(list => setProjects(list ?? []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const createProject = async () => {
    if (!newName.trim()) return
    const token = localStorage.getItem('echo_session')
    if (!token) return
    setCreating(true)
    try {
      const slug = newIndustry.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'custom'
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), industry: slug }),
      })
      if (res.ok) {
        const proj = await res.json() as { project_id: string }
        navigate(`/app/projects/${proj.project_id}/upload`)
      }
    } catch { /* ignore */ } finally {
      setCreating(false)
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl pb-20 md:pb-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Projects</h1>
        <Button variant="primary" onClick={() => setShowForm(v => !v)}>
          {showForm ? 'Cancel' : '+ New project'}
        </Button>
      </div>

      {/* Inline new project form */}
      {showForm && (
        <Card className="space-y-4 border-echo-indigo/20 bg-echo-indigo/3">
          <h2 className="text-sm font-medium text-deep-ink">New project</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Project name</label>
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Q3 Pricing Study"
                autoFocus
                className="w-full px-3 py-2 border border-border-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-echo-mark/30 focus:border-echo-mark bg-white placeholder:text-slate-400"
                onKeyDown={e => { if (e.key === 'Enter') void createProject() }}
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Industry</label>
              <select
                value={newIndustry}
                onChange={e => setNewIndustry(e.target.value)}
                className="w-full px-3 py-2 border border-border-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-echo-mark/30 focus:border-echo-mark bg-white text-slate-700"
              >
                <option value="">Select industry</option>
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>
          <p className="text-xs text-slate-400">After creating, you'll be taken to upload your first file for this project.</p>
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={!newName.trim() || creating}
              onClick={createProject}
            >
              {creating ? 'Creating…' : 'Create & upload →'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <Card className="text-center py-10">
          <p className="text-sm text-slate-400">Loading projects…</p>
        </Card>
      )}

      {/* Empty state */}
      {!loading && projects.length === 0 && !showForm && (
        <Card className="text-center py-12 space-y-3">
          <p className="text-sm font-medium text-deep-ink">No projects yet</p>
          <p className="text-xs text-slate-500 max-w-xs mx-auto">
            Create a project for each research study or client engagement, then upload your data files into it.
          </p>
          <div className="pt-2">
            <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>Create your first project →</Button>
          </div>
        </Card>
      )}

      {/* Project grid */}
      {!loading && projects.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(p => (
            <Link key={p.project_id} to={`/app/projects/${p.project_id}/echo-panel`}>
              <Card className="h-full hover:border-human-teal/40 transition-colors cursor-pointer space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-deep-ink leading-snug">{p.name}</p>
                  <Badge
                    variant={p.status === 'active' ? 'success' : 'default'}
                    size="sm"
                    className="shrink-0"
                  >
                    {p.status ?? 'active'}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400 capitalize">{p.industry?.replace(/-/g, ' ') ?? '—'}</p>
                <div className="pt-2 border-t border-border-gray flex items-center justify-between text-xs text-slate-400">
                  <span>Created {new Date(p.created_at).toLocaleDateString()}</span>
                  {p.first_upload_at
                    ? <span className="text-validation-green">Has data</span>
                    : <span className="text-uncertainty-amber">No data yet</span>}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
